import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useApiData, apiFetch } from "../lib/api.js";
import { getCurrentUser, setViewAsRole } from "../lib/auth.js";
import { useOperationsNotifikaceSeStavem, NotifikacePanelBody } from "./OperationsNotifikace.jsx";
import NotifPanel from "./NotifPanel.jsx";
import { useTrenerNotifikace, TrenerNotifRadek } from "./TrenerBell.jsx";

// Zvoneček pro managera a CSM — STEJNÁ logika jako ownerův přehled (taby
// Aktivní / Odložené / Archiv, odložit, archivovat, vrátit), ale jen nad
// JEJICH notifikacemi z Operations úkolů. Manager navíc vidí trenérské úkoly
// po termínu (head coach agenda). Stav drží OperationsNotifikace (localStorage).
const normName = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ManagerBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();
  // 30. 10. 2026 (#186): manažer, který sám trénuje (napojený trenér, dnes Michal), má
  // v jednom zvonečku manažerské i trenérské notifikace, v roli Manager i v roli Trenér.
  // Odkaz z notifikace druhé role nejdřív přepne roli a pak otevře správnou stránku.
  const realRole = String(user?._realRole || user?.role || "").toLowerCase();
  const linked = String(user?.linkedTrener || "").trim();
  const sjednoceny = realRole === "manager" && !!linked;
  const vRoliTrener = sjednoceny && String(user?.role || "") === "trener";
  const role = sjednoceny ? "manager" : String(user?.role || "").toLowerCase();
  // Trenérské úkoly po termínu jen pro managera.
  const { data: tasksData, refetch: refetchTasks } = useApiData(role === "manager" ? "/api/trener-ukoly" : null);
  const { data: hrData } = useApiData(role === "manager" ? "/api/hr-list-people?filter=treneri&status=aktivni" : null);

  const activeSet = useMemo(
    () => new Set(((hrData && hrData.people) || []).map((p) => normName(p.display_name))),
    [hrData]
  );
  const trenerske = useMemo(() => {
    const list = [];
    const tasks = (tasksData && tasksData.tasks) || [];
    const t0 = todayStr();
    for (const t of tasks
      .filter((t) => {
        const st = String(t.status || "naplanovane");
        if (st === "hotovo" || st === "archiv") return false;
        if (activeSet.size > 0 && !activeSet.has(normName(t.pilot || ""))) return false;
        // Vlastní trenérské úkoly manažera, který trénuje, má v manažerské nástěnce (#186).
        if (sjednoceny && normName(t.pilot || "") === normName(linked)) return false;
        return t.deadline && String(t.deadline).slice(0, 10) < t0;
      })
      .sort((a, b) => (String(a.deadline) < String(b.deadline) ? -1 : 1))) {
      list.push({
        id: `trener-${t.id}`,
        ikona: "🏋️",
        title: `Trenérský úkol po termínu: ${t.nazev || "(bez názvu)"}`,
        detail: `${t.pilot || "(bez trenéra)"}${t.klient_name ? ", " + t.klient_name : ""}`,
        link: "/manager/treneri/ukoly",
        // Přímé označení jako hotové z notifikace (jen manager).
        hotovo: async () => {
          const res = await apiFetch("/api/trener-ukoly", {
            method: "POST",
            body: JSON.stringify({ id: t.id, status: "hotovo" }),
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(j.error || "Nepodařilo se označit jako hotové.");
          if (j.redirected) throw new Error("Úkol vyžaduje kontrolu, přesunut Ke kontrole.");
          refetchTasks?.();
          return j;
        },
      });
    }
    return list;
  }, [tasksData, activeSet, sjednoceny, linked]);

  // 1. 11. 2026: nespokojená klientka po nutriční konzultaci (1 až 3 hvězdy v Hodnocení výživy).
  // Jen CSM (Kristina), zároveň jí jde Slack DM. Archivace a odložení jako u ostatních notifikací.
  const { data: hvData } = useApiData(role === "csm" ? "/api/hodnoceni-vyzivy?akce=upozorneni" : null, { bezKese: true });
  const sTrenerskymi = useMemo(() => {
    const hv = ((hvData && hvData.upozorneni) || []).map((h) => ({
      id: `hodnoceni-vyzivy-${h.id}`,
      ikona: "⭐",
      title: `Nespokojená po nutriční konzultaci: ${h.klient_name}`,
      // Odpovědi přímo ve zvonečku, ať se dá hned řešit (zadání Kristiny).
      detail: `${h.hvezdy} z 5 hvězd${h.nutricni ? `, ${h.nutricni}` : ""}${h.volala ? `, volala ${h.volala}` : ""}${h.odpovedi_kratce ? `. ${h.odpovedi_kratce}` : ""}`,
      link: `/clients?search=${encodeURIComponent(h.klient_name || "")}&open=1`,
    }));
    return hv.length ? [...trenerske, ...hv] : trenerske;
  }, [trenerske, hvData]);

  const stav = useOperationsNotifikaceSeStavem(sTrenerskymi, sjednoceny ? "manager" : "");
  const tren = useTrenerNotifikace(sjednoceny ? linked : "");
  const count = stav.counts["aktivní"] + (sjednoceny ? tren.active.length : 0);

  // Přechod s přepnutím role (celé načtení, stejně jako přepínač rolí v liště).
  const doRole = (cilovaRole, link) => {
    setOpen(false);
    if (!link) return;
    setViewAsRole(cilovaRole === "manager" ? null : cilovaRole);
    window.location.href = link;
  };
  const otevriTrenerskou = (link) => {
    if (!link) { setOpen(false); return; }
    // Trenérskou Fakturaci manažer nemá, výplatu vidí v Moje výplata (role Manager).
    if (String(link).startsWith("/trener/fakturace")) {
      if (vRoliTrener) doRole("manager", "/moje-vyplata"); else { setOpen(false); navigate("/moje-vyplata"); }
      return;
    }
    if (vRoliTrener) { setOpen(false); navigate(link); } else doRole("trener", link);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); if (sjednoceny && !open) tren.loadActive(); }}
        className="relative flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl hover:bg-slate-100"
        aria-label="Notifikace"
        title="Notifikace"
      >
        <Bell className={`h-6 w-6 ${count > 0 ? "text-red-600" : "text-slate-600"}`} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold leading-none px-1 tabular-nums">
            {count}
          </span>
        )}
      </button>

      {/* Panel: na mobilu přes celou obrazovku, na desktopu dropdown (NotifPanel). */}
      <NotifPanel open={open} onClose={() => setOpen(false)} count={count}>
        <NotifikacePanelBody stav={stav} kompakt onNavigate={() => setOpen(false)}
          prejdi={vRoliTrener ? (link) => doRole("manager", link) : null}
          dalsiPocet={sjednoceny ? tren.active.length : 0}
          dalsiAktivni={sjednoceny && (
            <div className="border-t border-slate-100">
              {tren.active.map((n) => (
                <TrenerNotifRadek key={n.notif_key} n={n} busy={tren.busy} act={tren.act} onOpen={otevriTrenerskou}
                  stitek={<span className="mb-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">Trenér</span>}
                  otevritText={vRoliTrener ? "" : "Otevřít jako Trenér →"} />
              ))}
            </div>
          )} />
      </NotifPanel>
    </div>
  );
}
